import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import { Card, Button } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import type {
  CustodySchedule,
  CustodyExceptionListResponse,
  CustodyException,
  CustodyTimeBlock,
  UserResponse,
  HouseholdResponse,
  BeneficiaryResponse,
} from '@/lib/types';
import {
  SCHEDULE_TYPE_NAMES,
  SCHEDULE_TYPE_DESCRIPTIONS,
  SCHEDULE_PATTERNS,
  getSegments,
} from '@/lib/types';

const DAY_OF_WEEK_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Compact 12-hour time (drops :00) to match the mobile app's time-block labels.
function formatBlockTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return minutes === '00' ? `${hour} ${ampm}` : `${hour}:${minutes} ${ampm}`;
}

function ScheduleDetailPage() {
  const { scheduleId } = Route.useParams();
  const id = Number(scheduleId);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track screen view
  useEffect(() => {
    track('Custody Schedule Detail Screen');
  }, []);

  // Fetch schedule
  const scheduleQuery = useQuery({
    queryKey: ['custody-schedule', id],
    queryFn: () => apiJson<CustodySchedule>(getToken, `/v1/schedules/${id}`),
    enabled: Number.isFinite(id) && id > 0,
  });

  // Fetch exceptions
  const exceptionsQuery = useQuery({
    queryKey: ['custody-exceptions', id],
    queryFn: () => apiJson<CustodyExceptionListResponse>(getToken, `/v1/schedules/${id}/exceptions`),
    enabled: Number.isFinite(id) && id > 0,
  });

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  const schedule = scheduleQuery.data;
  const exceptions = exceptionsQuery.data?.exceptions ?? [];
  const currentUser = userQuery.data;
  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];

  // Find the beneficiary for this schedule
  const beneficiary: BeneficiaryResponse | undefined = firstHousehold?.beneficiaries.find(
    (b) => b.beneficiary_id === schedule?.beneficiary_id
  );

  const getUserName = (userId: number): string => {
    if (!firstHousehold) return `User ${userId}`;
    const member = firstHousehold.members.find((m) => m.user_id === userId);
    if (!member) return `User ${userId}`;
    if (member.first_name && member.last_name) {
      return `${member.first_name} ${member.last_name}`;
    }
    if (member.first_name) {
      return member.first_name;
    }
    return `User ${userId}`;
  };

  const getParentName = (userId: number): string => {
    if (userId === currentUser?.id) return 'You';
    return getUserName(userId);
  };

  // Format time string (HH:MM:SS) for display
  const formatTimeDisplay = (timeStr: string | null): string => {
    if (!timeStr) return 'Not set';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Delete schedule mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!schedule) throw new Error('Schedule not found');
      return apiJson(getToken, `/v1/beneficiaries/${schedule.beneficiary_id}/schedule`, {
        method: 'DELETE',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-schedules'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error ? err.message : 'Could not delete schedule.';
      alert(errorMessage);
    },
  });

  // Approve exception mutation
  const approveExceptionMutation = useMutation({
    mutationFn: async (exceptionId: number) => {
      return apiJson(getToken, `/v1/exceptions/${exceptionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'APPROVED' }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-exceptions', id] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error ? err.message : 'Could not approve exception.';
      alert(errorMessage);
    },
  });

  // Reject exception mutation
  const rejectExceptionMutation = useMutation({
    mutationFn: async (exceptionId: number) => {
      return apiJson(getToken, `/v1/exceptions/${exceptionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'REJECTED' }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-exceptions', id] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error ? err.message : 'Could not reject exception.';
      alert(errorMessage);
    },
  });

  // Delete exception mutation
  const deleteExceptionMutation = useMutation({
    mutationFn: async (exceptionId: number) => {
      return apiJson(getToken, `/v1/exceptions/${exceptionId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-exceptions', id] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error ? err.message : 'Could not delete exception.';
      alert(errorMessage);
    },
  });

  const handleDelete = () => {
    if (window.confirm(`Delete this custody schedule for ${beneficiary?.name ?? 'this child'}? This action cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  const handleApproveException = (exceptionId: number) => {
    approveExceptionMutation.mutate(exceptionId);
  };

  const handleRejectException = (exceptionId: number) => {
    if (window.confirm('Reject this exception request?')) {
      rejectExceptionMutation.mutate(exceptionId);
    }
  };

  const handleDeleteException = (exceptionId: number) => {
    if (window.confirm('Delete this exception request?')) {
      deleteExceptionMutation.mutate(exceptionId);
    }
  };

  const canDelete = schedule && currentUser && schedule.creator_user_id === currentUser.id;

  // Sort exceptions: pending first, then by date
  const sortedExceptions = [...exceptions].sort((a, b) => {
    if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
    if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
    return new Date(a.exception_date).getTime() - new Date(b.exception_date).getTime();
  });

  const isLoading = scheduleQuery.isLoading || householdsQuery.isLoading || userQuery.isLoading;

  if (isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (scheduleQuery.isError || !schedule) {
    return (
      <div className='min-h-screen bg-sky-light'>
        <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
          <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
            <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
              <FiArrowLeft /> Back to Calendar
            </Link>
          </div>
        </header>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-8'>
          <Card className='text-center py-8'>
            <p className='text-gray-500'>Could not load this schedule.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Header */}
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6 space-y-4'>
        {/* Exceptions Section */}
        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
            Exceptions
          </h2>
          {exceptionsQuery.isLoading ? (
            <div className='flex justify-center py-4'>
              <div className='animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full' />
            </div>
          ) : sortedExceptions.length === 0 ? (
            <p className='text-gray-500 text-sm'>No exceptions for this schedule.</p>
          ) : (
            <div className='space-y-3'>
              {sortedExceptions.map((exception) => (
                <ExceptionCard
                  key={exception.id}
                  exception={exception}
                  currentUserId={currentUser?.id}
                  getParentName={getParentName}
                  getUserName={getUserName}
                  formatTimeDisplay={formatTimeDisplay}
                  timeBlocks={schedule.time_blocks ?? []}
                  onApprove={handleApproveException}
                  onReject={handleRejectException}
                  onDelete={handleDeleteException}
                  isMutating={
                    approveExceptionMutation.isPending ||
                    rejectExceptionMutation.isPending ||
                    deleteExceptionMutation.isPending
                  }
                />
              ))}
            </div>
          )}
        </Card>

        {/* Schedule Info */}
        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-1'>For</h2>
          <p className='text-lg font-bold text-gray-900'>{beneficiary?.name ?? 'Unknown'}</p>
        </Card>

        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-1'>Schedule Type</h2>
          <p className='text-lg font-bold text-gray-900'>
            {SCHEDULE_TYPE_NAMES[schedule.schedule_type] ?? schedule.schedule_type}
          </p>
          <p className='text-sm text-gray-500 mt-1'>
            {SCHEDULE_TYPE_DESCRIPTIONS[schedule.schedule_type] ?? ''}
          </p>
        </Card>

        {/* Custody Time Blocks (TIME_BLOCKS schedules) */}
        {schedule.schedule_type === 'TIME_BLOCKS' &&
          schedule.time_blocks &&
          schedule.time_blocks.length > 0 && (
            <Card>
              <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
                Custody Time Blocks
              </h2>
              <div className='space-y-3'>
                {schedule.time_blocks.map((tb) => {
                  const dayStart = DAY_OF_WEEK_SHORT[tb.day_of_week] ?? '?';
                  const start = formatBlockTime(tb.start_time);
                  const end = formatBlockTime(tb.end_time);
                  const dayEnd = tb.spans_next_day
                    ? DAY_OF_WEEK_SHORT[(tb.day_of_week + 1) % 7]
                    : null;
                  const label = dayEnd
                    ? `${dayStart} ${start} – ${dayEnd} ${end}`
                    : `${dayStart} ${start} – ${end}`;
                  const recurrence =
                    tb.recurrence_type === 'BIWEEKLY'
                      ? `Every other week (week ${(tb.recurrence_week ?? 0) + 1})`
                      : 'Every week';
                  return (
                    <div key={tb.id} className='p-3 bg-gray-50 rounded-xl'>
                      <div className='flex items-center justify-between gap-3'>
                        <span className='font-semibold text-gray-900 text-sm'>{label}</span>
                        <span className='text-sm text-gray-700 text-right'>
                          {getParentName(tb.assigned_user_id)}
                        </span>
                      </div>
                      <p className='text-xs text-gray-500 mt-1'>{recurrence}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

        <Card>
          <div className='flex justify-between py-2'>
            <span className='text-gray-500'>Start Date</span>
            <span className='font-semibold text-gray-900'>
              {new Date(schedule.start_date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
          <div className='h-px bg-gray-100 my-1' />
          <div className='flex justify-between py-2'>
            <span className='text-gray-500'>Starts With</span>
            <span className='font-semibold text-gray-900'>
              {getParentName(schedule.first_parent_user_id)}
            </span>
          </div>
        </Card>

        {/* Transition Times */}
        {(() => {
          const tt = schedule.transition_times;
          const hasTransitionTimes = tt && tt.length > 0 && tt.some((t) => t.time);

          if (hasTransitionTimes) {
            const pattern = SCHEDULE_PATTERNS[schedule.schedule_type] as number[] | undefined;
            const segments = pattern ? getSegments(pattern) : [];

            const parents = firstHousehold?.members ?? [];
            const resolvedFirstId = schedule.first_parent_user_id;
            const firstParent = parents.find((m) => m.user_id === resolvedFirstId);
            const secondParent = parents.find((m) => m.user_id !== resolvedFirstId);
            const name0 = firstParent?.first_name || 'Parent A';
            const name1 = secondParent?.first_name || 'Parent B';

            return (
              <Card>
                <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
                  Transition Times
                </h2>
                <div className='space-y-3'>
                  {tt.map((entry, idx) => {
                    const fromSeg = segments[idx];
                    const toSeg = segments[(idx + 1) % segments.length];
                    const fromName = fromSeg ? (fromSeg.parent === 0 ? name0 : name1) : '?';
                    const toName = toSeg ? (toSeg.parent === 0 ? name0 : name1) : '?';

                    return (
                      <div key={idx} className='p-3 bg-gray-50 rounded-xl'>
                        <div className='flex items-center gap-2 mb-1.5'>
                          <span className='w-5 h-5 rounded-full bg-gray-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0'>
                            {idx + 1}
                          </span>
                          <span className='text-sm text-gray-700'>
                            {fromName}&apos;s {fromSeg?.length ?? '?'} {(fromSeg?.length ?? 0) === 1 ? 'day' : 'days'}{' '}
                            &rarr; {toName}&apos;s {toSeg?.length ?? '?'} {(toSeg?.length ?? 0) === 1 ? 'day' : 'days'}
                          </span>
                        </div>
                        <div className='flex justify-between text-sm pl-7'>
                          <span className='text-gray-500'>
                            {entry.on_first_day
                              ? `On ${toName}'s first day`
                              : `On ${fromName}'s last day`}
                          </span>
                          <span className='font-semibold text-gray-900'>
                            {entry.time ? formatTimeDisplay(entry.time) : 'Not set'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          }

          // Fallback to legacy pickup_time / dropoff_time
          if (schedule.pickup_time || schedule.dropoff_time) {
            return (
              <Card>
                <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
                  Transition Times
                </h2>
                {schedule.pickup_time && (
                  <div className='flex justify-between py-2'>
                    <span className='text-gray-500'>Pickup Time</span>
                    <span className='font-semibold text-gray-900'>
                      {formatTimeDisplay(schedule.pickup_time)}
                    </span>
                  </div>
                )}
                {schedule.pickup_time && schedule.dropoff_time && (
                  <div className='h-px bg-gray-100 my-1' />
                )}
                {schedule.dropoff_time && (
                  <div className='flex justify-between py-2'>
                    <span className='text-gray-500'>Dropoff Time</span>
                    <span className='font-semibold text-gray-900'>
                      {formatTimeDisplay(schedule.dropoff_time)}
                    </span>
                  </div>
                )}
              </Card>
            );
          }

          return null;
        })()}

        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-1'>Created by</h2>
          <p className='text-lg font-bold text-gray-900'>{getUserName(schedule.creator_user_id)}</p>
          <p className='text-sm text-gray-500 mt-1'>
            {new Date(schedule.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </Card>

        {/* Delete Button */}
        {canDelete && (
          <Button
            variant='danger'
            fullWidth
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            Delete Schedule
          </Button>
        )}
      </div>
    </div>
  );
}

// Exception Card Component
function ExceptionCard({
  exception,
  currentUserId,
  getParentName,
  getUserName,
  formatTimeDisplay,
  timeBlocks,
  onApprove,
  onReject,
  onDelete,
  isMutating,
}: {
  exception: CustodyException;
  currentUserId?: number;
  getParentName: (id: number) => string;
  getUserName: (id: number) => string;
  formatTimeDisplay: (timeStr: string | null) => string;
  timeBlocks: CustodyTimeBlock[];
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onDelete: (id: number) => void;
  isMutating: boolean;
}) {
  const isCreator = exception.creator_user_id === currentUserId;
  const isPending = exception.status === 'PENDING';
  const canApproveReject = !isCreator && isPending;
  const canDelete = isCreator && isPending;

  const blockOverrides = exception.block_time_overrides ?? [];
  const cancelledBlocks = blockOverrides.filter((entry) => entry.cancelled);
  const timedBlocks = blockOverrides.filter((entry) => !entry.cancelled);
  const isCancelOnlyTimeChange =
    exception.exception_type === 'TIME_CHANGE' &&
    cancelledBlocks.length > 0 &&
    timedBlocks.length === 0;
  const isMixedTimeChange =
    exception.exception_type === 'TIME_CHANGE' &&
    cancelledBlocks.length > 0 &&
    timedBlocks.length > 0;

  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };

  return (
    <div className='p-4 bg-gray-50 rounded-xl border border-gray-200'>
      <div className='flex items-start justify-between mb-2'>
        <span
          className={`px-2 py-0.5 rounded-lg text-xs font-bold uppercase ${
            statusColors[exception.status as keyof typeof statusColors] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          {exception.status}
        </span>
      </div>
      <p className='font-semibold text-gray-900'>
        {new Date(exception.exception_date + 'T12:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </p>
      <p className='text-sm text-gray-600 mt-1'>
        {exception.exception_type === 'TIME_CHANGE' ? (
          isCancelOnlyTimeChange ? (
            <>Cancels time block.</>
          ) : isMixedTimeChange ? (
            <>Time change + block cancellation.</>
          ) : (
            <>Keeps custody: {getParentName(exception.assigned_user_id)}</>
          )
        ) : exception.exception_type === 'PARTIAL' ? (
          <>
            Visitation for {getParentName(exception.assigned_user_id)} (rest of the day keeps
            its normal custody)
          </>
        ) : (
          <>
            {exception.status === 'PENDING' && 'Would have custody: '}
            {exception.status === 'APPROVED' && 'Has custody: '}
            {exception.status === 'REJECTED' && 'Would have had custody: '}
            {getParentName(exception.assigned_user_id)}
          </>
        )}
      </p>

      {exception.time_windows.length > 0 && (
        <div className='flex flex-wrap gap-2 mt-2'>
          {exception.time_windows.map((window) => (
            <span
              key={window.id}
              className='inline-flex items-center px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold'
            >
              {formatTimeDisplay(window.start_time)} – {formatTimeDisplay(window.end_time)}
              {window.spans_next_day ? ' +1d' : ''}
            </span>
          ))}
        </div>
      )}

      {blockOverrides.length > 0 && (
        <div className='text-sm text-gray-600 mt-2'>
          {blockOverrides.map((override, idx) => {
            if (override.cancelled) {
              const block = timeBlocks.find((tb) => tb.id === override.block_id);
              const blockLabel = block
                ? `${getParentName(block.assigned_user_id)} ${formatBlockTime(
                    block.start_time
                  )}–${formatBlockTime(block.end_time)}`
                : `Block ${override.block_id}`;
              return (
                <p key={idx}>
                  <span className='font-semibold'>Canceled:</span> {blockLabel}
                </p>
              );
            }
            return (
              <p key={idx}>
                <span className='font-semibold'>Block {override.block_id}:</span>{' '}
                {override.start_time
                  ? `starts ${formatBlockTime(override.start_time)}`
                  : 'start unchanged'}
                {', '}
                {override.end_time
                  ? `ends ${formatBlockTime(override.end_time)}`
                  : 'end unchanged'}
              </p>
            );
          })}
        </div>
      )}

      {exception.reason && (
        <p className='text-sm text-gray-600 mt-2'>
          <span className='font-semibold'>Reason:</span> {exception.reason}
        </p>
      )}

      {(exception.pickup_time || exception.dropoff_time) && (
        <div className='flex gap-4 text-sm text-gray-600 mt-2'>
          {exception.pickup_time && (
            <span>Pickup: {formatTimeDisplay(exception.pickup_time)}</span>
          )}
          {exception.dropoff_time && (
            <span>Dropoff: {formatTimeDisplay(exception.dropoff_time)}</span>
          )}
        </div>
      )}

      <p className='text-xs text-gray-500 mt-2'>
        Requested by {getUserName(exception.creator_user_id)} on{' '}
        {new Date(exception.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>

      {exception.responded_at && (
        <p className='text-xs text-gray-500 mt-1'>
          {exception.status === 'APPROVED' ? 'Approved' : 'Rejected'} by{' '}
          {getUserName(exception.responded_by_user_id ?? 0)} on{' '}
          {new Date(exception.responded_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      )}

      {(canApproveReject || canDelete) && (
        <div className='flex gap-2 mt-3'>
          {canApproveReject && (
            <>
              <Button
                size='sm'
                onClick={() => onApprove(exception.id)}
                disabled={isMutating}
                className='flex-1 !bg-green-500 hover:!bg-green-600'
              >
                Approve
              </Button>
              <Button
                size='sm'
                variant='danger'
                onClick={() => onReject(exception.id)}
                disabled={isMutating}
                className='flex-1 !bg-red-500 hover:!bg-red-600 !text-white'
              >
                Reject
              </Button>
            </>
          )}
          {canDelete && (
            <Button
              size='sm'
              variant='danger'
              onClick={() => onDelete(exception.id)}
              disabled={isMutating}
              className='flex-1 !bg-red-500 hover:!bg-red-600 !text-white'
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/$scheduleId')({
  component: ScheduleDetailPage,
});
